import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { TranslocoDirective } from '@jsverse/transloco';
import { ButtonModule } from 'primeng/button';
import { DrawerModule } from 'primeng/drawer';
import { filter } from 'rxjs/operators';

import { DefaultLayoutNavigationComponent } from './default-layout-navigation.component';
import { heroArchiveBox } from '@ng-icons/heroicons/outline';
import { PtLanguageSelectorComponent } from '@pt/components/language-selector';
import { PtThemeSwitcherComponent } from '@pt/components/theme-switcher';
import { UserProfileComponent } from '../../common/user-profile/user-profile.component';

@Component({
  selector: 'app-default-layout',
  imports: [
    ButtonModule,
    DefaultLayoutNavigationComponent,
    DrawerModule,
    NgIcon,
    RouterOutlet,
    TranslocoDirective,
    PtLanguageSelectorComponent,
    PtThemeSwitcherComponent,
    UserProfileComponent,
  ],
  templateUrl: './default-layout.component.html',
  styleUrl: './default-layout.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [
    provideIcons({
      heroArchiveBox,
    }),
  ],
  host: {
    class: 'flex min-h-screen w-full',
  },
})
/** Default shell used by authenticated app pages. */
export class DefaultLayoutComponent {
  private readonly router = inject(Router);

  /** Controls mobile drawer visibility. */
  readonly isMobileNavigationOpen = signal(false);

  /** Controls desktop sidebar visibility. */
  readonly isDesktopNavigationOpen = signal(true);

  /** Stable current year used by the layout footer. */
  readonly currentYear = new Date().getFullYear();

  /** PrimeNG pass-through styles used to theme the mobile navigation drawer instance. */
  readonly mobileDrawerPassThrough = {
    root: {
      style: {
        background: 'var(--p-surface-900)',
        borderInlineStart: '1px solid var(--default-layout-mobile-drawer-border-color)',
        color: 'var(--p-surface-0)',
      },
    },
    header: {
      style: {
        borderBottom: '1px solid var(--default-layout-mobile-drawer-header-border-color)',
        color: 'var(--p-surface-0)',
        padding: '0.5rem 0.65rem',
      },
    },
    content: {
      style: {
        padding: '0',
      },
    },
  } as const;

  constructor() {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe(() => {
        this.isMobileNavigationOpen.set(false);
      });
  }

  /** Opens the mobile drawer menu. */
  openMobileNavigation(): void {
    this.isMobileNavigationOpen.set(true);
  }

  /** Closes the mobile drawer menu. */
  closeMobileNavigation(): void {
    this.isMobileNavigationOpen.set(false);
  }

  /** Syncs the mobile drawer state with PrimeNG `visibleChange` events. */
  setMobileNavigationVisibility(visible: boolean): void {
    this.isMobileNavigationOpen.set(visible);
  }

  /** Toggles desktop sidebar visibility. */
  toggleDesktopNavigation(): void {
    this.isDesktopNavigationOpen.update((opened) => !opened);
  }

  /** Handles route leaf activations emitted by the sidebar component. */
  handleNavigationItemActivated(): void {
    this.closeMobileNavigation();
  }
}
